/**
 * Per-variant barcode management (SPEC screen 2, Barcodes tab).
 * Scan-or-type input via the shared scanner wedge, symbology auto-suggest,
 * client check-digit gate, 409 `barcode_duplicate` inline with owning-variant
 * link, replay-safe add (Idempotency-Key held across retries).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";
import { useMutation } from "@tanstack/react-query";

import { surfaceProblem } from "@/components/form";
import { ApiError } from "@/lib/api/errors";
import { useIdempotentMutation } from "@/features/shared/ops-api";

// Shared scanner utility — relative import per the workspace contract.
import { useScannerInput } from "../../shared/scanner/src/react";
import {
  detectSymbology,
  isValidCode128,
  isValidEan13,
  isValidGtin14,
  isValidUpcA,
} from "../../shared/scanner/src/symbology";

import {
  BARCODE_TYPES,
  addBarcode,
  removeBarcode,
  type BarcodeType,
  type Variant,
  type VariantBarcode,
} from "../api";

/** Scanner symbology name → catalog wire enum (`UPCA` → `UPC`). */
function suggestType(value: string): BarcodeType | null {
  const symbology = detectSymbology(value);
  if (symbology === null) return null;
  return symbology === "UPCA" ? "UPC" : symbology;
}

function isValidForType(value: string, type: BarcodeType): boolean {
  switch (type) {
    case "EAN13":
      return isValidEan13(value);
    case "UPC":
      return isValidUpcA(value);
    case "GTIN14":
      return isValidGtin14(value);
    case "CODE128":
      return isValidCode128(value);
  }
}

interface DuplicateOwner {
  variant_id?: string;
  sku?: string;
}

export interface BarcodesPanelProps {
  variants: Variant[];
  /** The SELECTED variant's barcodes (GET /variants/{id}/barcodes). */
  barcodes: VariantBarcode[];
  selectedVariantId: string | null;
  /** Zero-hit scan handoff; exact value remains editable before submission. */
  initialBarcode?: string;
  onSelectVariant: (variantId: string) => void;
  /** Invalidate the barcode queries after an add/remove. */
  onChanged: () => void;
  /** 409 owner link → open the owning variant (may be another product). */
  onOpenOwner: (owner: DuplicateOwner, barcode: string) => void;
  canCreate?: boolean;
  canDelete?: boolean;
}

export function BarcodesPanel({
  variants,
  barcodes,
  selectedVariantId,
  initialBarcode,
  onSelectVariant,
  onChanged,
  onOpenOwner,
  canCreate = true,
  canDelete = true,
}: BarcodesPanelProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialBarcode ?? "");
  const [type, setType] = useState<BarcodeType>("EAN13");
  const [checkDigitError, setCheckDigitError] = useState(false);
  const [duplicate, setDuplicate] = useState<{ owner: DuplicateOwner; barcode: string } | null>(
    null,
  );

  const variant = variants.find((candidate) => candidate.id === selectedVariantId) ?? null;
  // Already scoped to the selected variant by the parent's per-variant query.
  const rows = barcodes;

  useEffect(() => {
    if (initialBarcode === undefined) return;
    setValue(initialBarcode);
    setCheckDigitError(false);
    setDuplicate(null);
    const suggested = suggestType(initialBarcode);
    if (suggested !== null) setType(suggested);
  }, [initialBarcode]);

  // A physical scan fills the field and auto-suggests the symbology.
  useScannerInput({
    onScan: (scan) => {
      setValue(scan.value);
      setCheckDigitError(false);
      setDuplicate(null);
      const suggested = suggestType(scan.value);
      if (suggested !== null) setType(suggested);
    },
  });

  const addMutation = useIdempotentMutation({
    mutationFn: (input: { variantId: string; barcode: string; type: BarcodeType }, key: string) =>
      addBarcode(input.variantId, { barcode: input.barcode, type: input.type }, key),
    onSuccess: () => {
      setValue("");
      setDuplicate(null);
      onChanged();
    },
    onError: (error, input) => {
      if (error instanceof ApiError && error.status === 409 && error.code === "barcode_duplicate") {
        const owner = (error.problem as { owner?: DuplicateOwner } | undefined)?.owner ?? {};
        setDuplicate({ owner, barcode: input.barcode });
        return;
      }
      surfaceProblem(error, t);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (input: { variantId: string; barcode: string }) =>
      removeBarcode(input.variantId, input.barcode),
    onSuccess: onChanged,
    onError: (error: unknown) => surfaceProblem(error, t),
  });

  const submit = () => {
    const trimmed = value.trim();
    if (variant === null || trimmed === "") return;
    if (!isValidForType(trimmed, type)) {
      // Client check-digit failure blocks submit (SPEC) — server stays authority.
      setCheckDigitError(true);
      return;
    }
    setCheckDigitError(false);
    addMutation.mutate({ variantId: variant.id, barcode: trimmed, type });
  };

  if (variants.length === 0) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        {t("catalog.barcodes.noVariants", "Save a variant first, then manage its barcodes.")}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="barcodes-variant">{t("catalog.barcodes.variant", "Variant")}</Label>
          <Select value={selectedVariantId ?? ""} onValueChange={onSelectVariant}>
            <SelectTrigger id="barcodes-variant" className="w-48">
              <SelectValue placeholder={t("catalog.barcodes.pickVariant", "Pick a variant")} />
            </SelectTrigger>
            <SelectContent>
              {variants.map((candidate) => (
                <SelectItem key={candidate.id} value={candidate.id}>
                  <span dir="ltr">{candidate.sku}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {canCreate ? (
          <>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="barcodes-value">
                {t("catalog.barcodes.value", "Scan or type barcode")}
              </Label>
              <Input
                id="barcodes-value"
                dir="ltr"
                className="w-56 font-mono"
                value={value}
                onChange={(event) => {
                  setValue(event.target.value);
                  setCheckDigitError(false);
                  setDuplicate(null);
                  const suggested = suggestType(event.target.value.trim());
                  if (suggested !== null) setType(suggested);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    submit();
                  }
                }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="barcodes-type">{t("catalog.barcodes.type", "Type")}</Label>
              <Select value={type} onValueChange={(next) => setType(next as BarcodeType)}>
                <SelectTrigger id="barcodes-type" className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BARCODE_TYPES.map((candidate) => (
                    <SelectItem key={candidate} value={candidate}>
                      <span dir="ltr">{candidate}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              onClick={submit}
              disabled={variant === null || value.trim() === "" || addMutation.isPending}
              loading={addMutation.isPending}
            >
              {t("catalog.barcodes.add", "Add")}
            </Button>
          </>
        ) : null}
      </div>

      {checkDigitError ? (
        <p role="alert" className="text-sm text-destructive">
          {t(
            "catalog.barcodes.checkDigitError",
            "Invalid check digit for {{type}} — fix the code before adding.",
            { type },
          )}
        </p>
      ) : null}

      {duplicate !== null ? (
        <p role="alert" className="flex flex-wrap items-center gap-1 text-sm text-destructive">
          <span>
            {t("catalog.barcodes.duplicate", "{{barcode}} is already used by variant {{sku}}.", {
              barcode: duplicate.barcode,
              sku: duplicate.owner.sku ?? t("catalog.barcodes.unknownOwner", "another variant"),
            })}
          </span>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-sm"
            onClick={() => onOpenOwner(duplicate.owner, duplicate.barcode)}
          >
            {t("catalog.barcodes.openOwner", "Open owning variant")}
          </Button>
        </p>
      ) : null}

      <ul className="flex flex-col divide-y rounded-md border">
        {rows.length === 0 ? (
          <li className="p-4 text-sm text-muted-foreground">
            {t("catalog.barcodes.empty", "No barcodes on this variant yet.")}
          </li>
        ) : (
          rows.map((row) => (
            <li key={row.id} className="flex items-center justify-between gap-3 p-2">
              <span className="flex items-center gap-3">
                <span dir="ltr" className="font-mono">
                  {row.barcode}
                </span>
                <span dir="ltr" className="text-xs text-muted-foreground">
                  {row.type}
                </span>
              </span>
              {canDelete ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t("catalog.barcodes.remove", "Remove barcode")}
                  disabled={removeMutation.isPending}
                  onClick={() =>
                    removeMutation.mutate({ variantId: row.variant_id, barcode: row.barcode })
                  }
                >
                  <Trash2 className="size-4" aria-hidden="true" />
                </Button>
              ) : null}
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
